import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  addDoc,
  serverTimestamp,
  query,
  where,
  limit,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useSession } from '../utils/SessionContext';

import {
  Select,
  MenuItem,
  FormControl,
  Button,
  CircularProgress,
  Tooltip,
  Box,
  Alert,
  IconButton,
  InputAdornment,
  Typography,
  Snackbar,
  Slide,
  Autocomplete,
  TextField,
} from '@mui/material';

import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import BookOutlinedIcon from '@mui/icons-material/BookOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import LoopAnimation from '../img/LoopAnimation.gif';

import NEXUSI from '../img/NEXUSI.jpg';
import NEXUSII from '../img/NEXUSII.jpg';
import NEXUSIII from '../img/NEXUSIII.jpg';
import NEXUSIV from '../img/NEXUSIV.jpg';

import SANDBOXI from '../img/SANDBOXI.jpg';
import SANDBOXII from '../img/SANDBOXII.jpg';
import SANDBOXIII from '../img/SANDBOXIII.jpg';
import SANDBOXIV from '../img/SANDBOXIV.jpg';

import RAISELABI from '../img/RAISELABI.jpg';
import RAISELABII from '../img/RAISELABII.jpg';
import RAISELABIII from '../img/RAISELABIII.jpg';
import RAISELABIV from '../img/RAISELABIV.jpg';

const LAB_OPTIONS = [
  {
    key: 'NEXUS',
    label: 'NEXUS',
    images: [NEXUSI, NEXUSII, NEXUSIII, NEXUSIV],
  },
  {
    key: 'RAISE LAB',
    label: 'RAISE LAB',
    images: [RAISELABI, RAISELABII, RAISELABIII, RAISELABIV],
  },
  {
    key: 'SAND BOX',
    label: 'SAND BOX',
    images: [SANDBOXI, SANDBOXII, SANDBOXIII, SANDBOXIV],
  },
];

const PAGE_BG = '#f7f7f7';

const SlideUpTransition = (props) => <Slide {...props} direction="up" />;

const EMPTY_SELECTIONS = {
  course: '',
  yearLevel: '',
  section: '',
  subject: '',
};

const normalizeText = (value) => (value || '').toString().trim().toLowerCase();

const sortAlphabetically = (items = [], getValue) =>
  [...items].sort((a, b) => {
    const aValue = normalizeText(getValue ? getValue(a) : a);
    const bValue = normalizeText(getValue ? getValue(b) : b);
    return aValue.localeCompare(bValue);
  });

const MAX_VISIBLE_ITEMS = 6;
const ITEM_HEIGHT = 44;

const ellipsisBoxSx = {
  display: 'block',
  width: '100%',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const menuPaperSx = {
  mt: 0.8,
  borderRadius: '14px',
  border: '2px solid rgba(14,79,183,0.12)',
  boxShadow: '0 18px 40px rgba(0,0,0,0.18)',
  backgroundColor: '#FFFFFF',
  overflow: 'hidden',
  '& .MuiAutocomplete-listbox, & .MuiList-root': {
    paddingTop: '6px',
    paddingBottom: '6px',
    maxHeight: ITEM_HEIGHT * MAX_VISIBLE_ITEMS + 12,
    overflowY: 'auto',
  },
  '& .MuiMenuItem-root, & .MuiAutocomplete-option': {
    fontFamily: '"Poppins", sans-serif',
    fontSize: '0.95rem',
    fontWeight: 500,
    minHeight: 40,
    height: 40,
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '14px',
    paddingRight: '14px',
    margin: '2px 6px',
    borderRadius: '10px',
    color: '#171717',
    transition: 'all 0.18s ease',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  '& .MuiMenuItem-root:hover, & .MuiAutocomplete-option:hover': {
    backgroundColor: 'rgba(14,79,183,0.08)',
  },
  '& .Mui-selected, & .Mui-focused': {
    backgroundColor: 'rgba(14,79,183,0.14) !important',
    color: '#0E4FB7',
    fontWeight: 600,
  },
};

const SessionSelection = () => {
  const { session, updateSession } = useSession();
  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);
  const [yearLevels, setYearLevels] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [profScanInput, setProfScanInput] = useState('');
  const [profLoading, setProfLoading] = useState(false);
  const [profError, setProfError] = useState('');

  const [professor, setProfessor] = useState(() => {
    if (!session?.prof && !session?.profId) return null;

    return {
      id: session?.profId || '',
      name: session?.prof || '',
      rfid: session?.profRfid || '',
      image: session?.profImage || '',
    };
  });

  const [selectedLab, setSelectedLab] = useState(session?.selectedLab || '');
  const [selected, setSelected] = useState(EMPTY_SELECTIONS);
  const [subjectInputValue, setSubjectInputValue] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [bgIndex, setBgIndex] = useState(0);

  const [toastOpen, setToastOpen] = useState(false);
  const [toastText, setToastText] = useState('');
  const [toastSeverity, setToastSeverity] = useState('info');

  const scanningEnabledRef = useRef(true);
  const hasClearedInitialSelections = useRef(false);

  const selectedLabConfig =
    LAB_OPTIONS.find((lab) => lab.key === selectedLab || lab.label === selectedLab) || null;

  const sortedCourses = useMemo(() => sortAlphabetically(courses), [courses]);
  const sortedYearLevels = useMemo(() => sortAlphabetically(yearLevels), [yearLevels]);
  const sortedSections = useMemo(() => sortAlphabetically(sections), [sections]);

  const filteredSubjects = useMemo(() => {
    if (!selected.course) return [];

    const matched = subjects.filter(
      (subject) => normalizeText(subject?.course) === normalizeText(selected.course)
    );

    return sortAlphabetically(matched, (subject) => subject?.description || '');
  }, [subjects, selected.course]);

  const selectedSubjectObj = useMemo(() => {
    if (!selected.subject) return null;

    return (
      subjects.find((subject) => {
        const code = (subject?.code || '').toString().trim();
        const description = (subject?.description || '').toString().trim();
        return code === selected.subject || description === selected.subject;
      }) || null
    );
  }, [subjects, selected.subject]);

  useEffect(() => {
    setSubjectInputValue(selectedSubjectObj?.description || '');
  }, [selectedSubjectObj]);

  useEffect(() => {
    if (hasClearedInitialSelections.current) return;
    hasClearedInitialSelections.current = true;

    setSelected(EMPTY_SELECTIONS);

    updateSession({
      course: '',
      yearLevel: '',
      section: '',
      subject: '',
      sessionId: '',
    });
  }, [updateSession]);

  useEffect(() => {
    if (!selectedLabConfig) {
      setBgIndex(0);
      return;
    }

    const savedLabImage = session?.labImage;
    if (!savedLabImage) {
      setBgIndex(0);
      return;
    }

    const matchedIndex = selectedLabConfig.images.findIndex((img) => img === savedLabImage);
    setBgIndex(matchedIndex >= 0 ? matchedIndex : 0);
  }, [selectedLabConfig, session?.labImage]);

  useEffect(() => {
    if (!selectedLabConfig?.images || selectedLabConfig.images.length <= 1) return undefined;

    const interval = setInterval(() => {
      setBgIndex((prev) => (prev + 1) % selectedLabConfig.images.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [selectedLabConfig]);

  const showToast = useCallback((text, severity = 'info') => {
    setToastText(text);
    setToastSeverity(severity);
    setToastOpen(true);
  }, []);

  const getProfessorImage = (data = {}) =>
    data.photoURL ||
    data.imageUrl ||
    data.image ||
    data.profileImage ||
    data.avatar ||
    data.picture ||
    data.photo ||
    '';

  const fetchData = async () => {
    setLoading(true);
    setError('');

    try {
      const settingsDoc = await getDoc(doc(db, 'Settings', 'global'));

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();

        setCourses(Array.isArray(data.courses) ? data.courses : []);
        setYearLevels(Array.isArray(data.yearLevels) ? data.yearLevels : []);
        setSections(Array.isArray(data.sections) ? data.sections : []);
        setSubjects(Array.isArray(data.subjects) ? data.subjects : []);
      } else {
        setError('Configuration data not found in database.');
        showToast('Configuration data not found in database.', 'error');
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data: ' + err.message);
      showToast('Failed to load data: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fieldSx = useMemo(
    () => ({
      width: '100%',
      '& .MuiOutlinedInput-root': {
        minHeight: { xs: 58, md: 56 },
        borderRadius: '14px',
        backgroundColor: '#F0F0F0',
        fontFamily: '"Poppins", sans-serif',
        fontSize: { xs: '0.98rem', md: '1.02rem' },
        color: '#171717',
        boxShadow: 'none',
        '& fieldset': {
          borderColor: '#0E4FB7',
          borderWidth: '2px',
        },
        '&:hover fieldset': {
          borderColor: '#0E4FB7',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#0E4FB7',
          borderWidth: '2px',
        },
      },
      '& .MuiSelect-select': {
        display: 'block',
        minHeight: '56px !important',
        paddingTop: '16px !important',
        paddingBottom: '16px !important',
        paddingLeft: '0 !important',
        paddingRight: '0 !important',
        fontFamily: '"Poppins", sans-serif !important',
        fontStyle: 'normal',
        fontWeight: 500,
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
      },
      '& .MuiOutlinedInput-input': {
        paddingTop: '16px',
        paddingBottom: '16px',
        paddingLeft: '0px',
        fontFamily: '"Poppins", sans-serif !important',
        fontStyle: 'normal',
        fontWeight: 500,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      '& .MuiSelect-icon': {
        display: 'none',
      },
    }),
    []
  );

  const dropdownMenuProps = useMemo(
    () => ({
      PaperProps: {
        elevation: 0,
        sx: menuPaperSx,
      },
      MenuListProps: {
        dense: true,
        disablePadding: false,
      },
    }),
    []
  );

  const handleChange = (e, field) => {
    const nextValue = e.target.value;

    setSelected((prev) => {
      if (field === 'course') {
        return {
          ...prev,
          course: nextValue,
          subject: '',
        };
      }

      return {
        ...prev,
        [field]: nextValue,
      };
    });

    if (field === 'course') {
      setSubjectInputValue('');
    }

    setError('');
  };

  const lookupProfessorByRfid = useCallback(
    async (rfidValue) => {
      setProfLoading(true);
      setProfError('');

      try {
        const q = query(collection(db, 'Faculties'), where('rfid', '==', rfidValue), limit(1));
        const snap = await getDocs(q);

        const setProf = (docSnap) => {
          const data = docSnap.data();
          const fullName =
            `${data.firstName || ''} ${data.lastName || ''}`.trim() ||
            data.name ||
            'Professor';

          const profObj = {
            id: docSnap.id,
            name: fullName,
            rfid: rfidValue,
            image: getProfessorImage(data),
          };

          setProfessor(profObj);
          setError('');

          updateSession({
            prof: fullName,
            profId: docSnap.id,
            profRfid: rfidValue,
            profImage: profObj.image,
            selectedLab: selectedLabConfig?.label || selectedLab,
            labImage: selectedLabConfig?.images?.[bgIndex] || session?.labImage || '',
            labImages: selectedLabConfig?.images || session?.labImages || [],
            sessionId: '',
          });

          showToast(`Welcome, ${fullName}.`, 'info');
        };

        if (!snap.empty) {
          setProf(snap.docs[0]);
          return;
        }

        const all = await getDocs(collection(db, 'Faculties'));
        const match = all.docs.find(
          (docu) => (docu.data().rfid || '').toString().trim().toLowerCase() === rfidValue
        );

        if (match) {
          setProf(match);
          return;
        }

        setProfError('Professor RFID not found. Please try again.');
        showToast('Professor RFID not found. Please try again.', 'error');
      } catch (e) {
        console.error('Professor lookup error:', e);
        setProfError('Failed to verify professor RFID: ' + e.message);
        showToast('Failed to verify professor RFID: ' + e.message, 'error');
      } finally {
        setProfLoading(false);
      }
    },
    [updateSession, showToast, selectedLabConfig, selectedLab, bgIndex, session?.labImage, session?.labImages]
  );

  const handleProfRfidScan = useCallback(
    async (e) => {
      if (!scanningEnabledRef.current) return;
      if (profLoading) return;

      if (e.key && e.key.length === 1 && /\d/.test(e.key)) {
        setProfScanInput((prev) => prev + e.key);
      } else if (e.key === 'Enter' && profScanInput) {
        const rfidValue = profScanInput.trim().toLowerCase();
        setProfScanInput('');
        await lookupProfessorByRfid(rfidValue);
      }
    },
    [profLoading, profScanInput, lookupProfessorByRfid]
  );

  useEffect(() => {
    scanningEnabledRef.current = Boolean(selectedLabConfig) && !professor;

    if (selectedLabConfig && !professor) {
      window.addEventListener('keydown', handleProfRfidScan);
      return () => window.removeEventListener('keydown', handleProfRfidScan);
    }

    return undefined;
  }, [professor, selectedLabConfig, handleProfRfidScan]);

  const resetProfessor = () => {
    setProfessor(null);
    setProfError('');
    setProfScanInput('');
    setError('');
    setSelected(EMPTY_SELECTIONS);
    setSubjectInputValue('');

    updateSession({
      prof: '',
      profId: '',
      profRfid: '',
      profImage: '',
      selectedLab: selectedLabConfig?.label || selectedLab,
      labImage: selectedLabConfig?.images?.[bgIndex] || session?.labImage || '',
      labImages: selectedLabConfig?.images || session?.labImages || [],
      course: '',
      yearLevel: '',
      section: '',
      subject: '',
      sessionId: '',
    });

    showToast('Professor selection has been reset.', 'warning');
  };

  const resetLaboratory = () => {
    setSelectedLab('');
    setProfessor(null);
    setProfError('');
    setProfScanInput('');
    setBgIndex(0);
    setError('');
    setSelected(EMPTY_SELECTIONS);
    setSubjectInputValue('');

    updateSession({
      selectedLab: '',
      labImage: '',
      labImages: [],
      prof: '',
      profId: '',
      profRfid: '',
      profImage: '',
      course: '',
      yearLevel: '',
      section: '',
      subject: '',
      sessionId: '',
    });

    showToast('Laboratory selection has been reset.', 'warning');
  };

  const handleLabSelect = (lab) => {
    const nextIndex =
      selectedLab === lab.label || selectedLab === lab.key
        ? (bgIndex + 1) % lab.images.length
        : 0;

    setSelectedLab(lab.label);
    setBgIndex(nextIndex);
    setError('');
    setSelected(EMPTY_SELECTIONS);
    setProfessor(null);
    setProfError('');
    setProfScanInput('');
    setSubjectInputValue('');

    updateSession({
      selectedLab: lab.label,
      labImage: lab.images[nextIndex],
      labImages: lab.images,
      prof: '',
      profId: '',
      profRfid: '',
      profImage: '',
      course: '',
      yearLevel: '',
      section: '',
      subject: '',
      sessionId: '',
    });
  };

  const isValidSubjectSelection = Boolean(selectedSubjectObj);

  const getMissingFields = () => {
    const missing = [];

    if (!selectedLabConfig) missing.push('Laboratory');
    if (!selected.course) missing.push('Course');
    if (!selected.yearLevel) missing.push('Year Level');
    if (!selected.section) missing.push('Section');
    if (!selected.subject) {
      missing.push('Subject');
    } else if (!isValidSubjectSelection) {
      missing.push('Valid Subject');
    }

    return missing;
  };

  const handleSubmit = async () => {
    if (!professor?.id) {
      setError('Professor is required.');
      showToast('Professor is required.', 'error');
      return;
    }

    const missingFields = getMissingFields();

    if (missingFields.length > 0) {
      const hasInvalidSubject = missingFields.includes('Valid Subject');

      const message = hasInvalidSubject
        ? 'Please select a valid subject from the suggestions.'
        : missingFields.length === 1
          ? `${missingFields[0]} is required.`
          : `Please complete the following first: ${missingFields.join(', ')}.`;

      setError(message);
      showToast(message, 'warning');
      return;
    }

    setError('');
    setLoading(true);

    const selectedImage = selectedLabConfig.images[bgIndex] || selectedLabConfig.images[0];
    const subjectToSave = selectedSubjectObj?.code || selected.subject;

    const updatedSession = {
      ...session,
      prof: professor.name,
      profId: professor.id,
      profRfid: professor.rfid,
      profImage: professor.image || '',
      selectedLab: selectedLabConfig.label,
      labImage: selectedImage,
      labImages: selectedLabConfig.images,
      course: selected.course,
      yearLevel: selected.yearLevel,
      section: selected.section,
      subject: subjectToSave,
    };

    updateSession(updatedSession);

    try {
      const sessionPayload = {
        prof: professor.name,
        profId: professor.id,
        profRfid: professor.rfid,
        selectedLab: selectedLabConfig.label,
        labImage: selectedImage,
        labImages: selectedLabConfig.images,
        course: selected.course,
        yearLevel: selected.yearLevel,
        section: selected.section,
        subject: subjectToSave,
        startTime: serverTimestamp(),
      };

      const newSession = await addDoc(collection(db, 'Sessions'), sessionPayload);

      updateSession({ ...updatedSession, sessionId: newSession.id });
      showToast('Session created successfully.', 'success');
      navigate(`/newsession/${newSession.id}`);
    } catch (err) {
      console.error('Error creating session:', err);
      setError('Failed to create session: ' + err.message);
      setLoading(false);
      showToast('Failed to create session: ' + err.message, 'error');
    }
  };

  const iconCluster = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 11px)',
        gridTemplateRows: 'repeat(2, 11px)',
        gap: '4px',
      }}
    >
      {[0, 1, 2, 3].map((i) => (
        <Box
          key={i}
          sx={{
            width: 11,
            height: 11,
            borderRadius: '3px',
            backgroundColor: '#D9AB24',
          }}
        />
      ))}
    </Box>
  );

  const renderClosedValue = (value, placeholder, titleValue) => (
    <Box
      component="span"
      sx={{
        ...ellipsisBoxSx,
        fontFamily: 'Poppins, sans-serif',
        fontWeight: 500,
        color: value ? '#171717' : '#7a7a7a',
      }}
      title={titleValue || value || placeholder}
    >
      {value || placeholder}
    </Box>
  );

  if (!selectedLabConfig) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          bgcolor: PAGE_BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 3, md: 4 },
        }}
      >
        <Snackbar
          open={toastOpen}
          autoHideDuration={5000}
          onClose={() => setToastOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          TransitionComponent={SlideUpTransition}
          sx={{
            '&.MuiSnackbar-root': {
              left: { xs: 16, sm: 24 },
              bottom: { xs: 16, sm: 24 },
            },
          }}
        >
          <Alert
            onClose={() => setToastOpen(false)}
            severity={toastSeverity}
            variant="filled"
            sx={{
              width: '100%',
              minWidth: { xs: 260, sm: 360 },
              maxWidth: { xs: 'calc(100vw - 32px)', sm: 460 },
              borderRadius: 2,
              boxShadow: '0 10px 24px rgba(0,0,0,0.25)',
              alignItems: 'center',
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 700,
              ...(toastSeverity === 'success' && {
                bgcolor: '#0f56b3',
                color: '#fff',
                '& .MuiAlert-icon': { color: '#fff' },
              }),
              ...(toastSeverity === 'info' && {
                bgcolor: '#0f56b3',
                color: '#fff',
                '& .MuiAlert-icon': { color: '#fff' },
              }),
              ...(toastSeverity === 'warning' && {
                bgcolor: '#ffc400',
                color: '#111',
                '& .MuiAlert-icon': { color: '#111' },
              }),
              ...(toastSeverity === 'error' && {
                bgcolor: '#b90d0d',
                color: '#fff',
                '& .MuiAlert-icon': { color: '#fff' },
              }),
            }}
          >
            {toastText}
          </Alert>
        </Snackbar>

        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            width: '100%',
            maxWidth: '560px',
          }}
        >
          <Box
            sx={{
              backgroundColor: 'rgba(242,188,9,0.96)',
              borderRadius: '18px',
              border: '4px solid #ffffff',
              boxShadow: '0 18px 40px rgba(0,0,0,0.12)',
              p: { xs: 2, sm: 2.5, md: 3 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.6 }}>
              <ApartmentOutlinedIcon sx={{ color: '#0E4FB7' }} />
              <Typography
                sx={{
                  fontFamily: '"Space Grotesk", sans-serif',
                  fontWeight: 900,
                  fontSize: { xs: '1rem', sm: '1.08rem' },
                  color: '#0E4FB7',
                  letterSpacing: '0.03em',
                }}
              >
                LABORATORY
              </Typography>
            </Box>

            <Typography
              sx={{
                mb: 1.8,
                color: '#4b5563',
                fontSize: '0.92rem',
                fontFamily: '"Poppins", sans-serif',
                lineHeight: 1.6,
              }}
            >
              Select the laboratory first before the professor taps their ID.
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: 1.4,
              }}
            >
              {LAB_OPTIONS.map((lab) => {
                const previewImage = lab.images[0];

                return (
                  <Button
                    key={lab.key}
                    onClick={() => handleLabSelect(lab)}
                    sx={{
                      minHeight: 112,
                      borderRadius: '16px',
                      overflow: 'hidden',
                      p: 0,
                      border: '2px solid #ffffff',
                      position: 'relative',
                      backgroundImage: `linear-gradient(rgba(0,0,0,0.24), rgba(0,0,0,0.24)), url(${previewImage})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      boxShadow: '0 10px 20px rgba(0,0,0,0.10)',
                      transition: 'all 0.18s ease',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: 'rgba(14,79,183,0)',
                        transition: 'background-color 0.18s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        '&::after': {
                          backgroundColor: 'rgba(14,79,183,0.22)',
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 1.5,
                        zIndex: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#fff',
                          fontFamily: '"Advetime", sans-serif',
                          fontWeight: 700,
                          fontSize: { xs: '1.2rem', sm: '1.2rem', md: '2.4rem' },
                          textAlign: 'center',
                          textShadow: '0 4px 14px rgba(0,0,0,0.45)',
                          letterSpacing: '0.03em',
                          lineHeight: 1.1,
                          opacity: 0.77,
                        }}
                      >
                        {lab.label}
                      </Typography>
                    </Box>
                  </Button>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  if (!professor) {
    return (
      <Box
        sx={{
          height: '100vh',
          width: '100%',
          bgcolor: PAGE_BG,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Snackbar
          open={toastOpen}
          autoHideDuration={5000}
          onClose={() => setToastOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          TransitionComponent={SlideUpTransition}
          sx={{
            '&.MuiSnackbar-root': {
              left: { xs: 16, sm: 24 },
              bottom: { xs: 16, sm: 24 },
            },
          }}
        >
          <Alert
            onClose={() => setToastOpen(false)}
            severity={toastSeverity}
            variant="filled"
            sx={{
              width: '100%',
              minWidth: { xs: 260, sm: 360 },
              maxWidth: { xs: 'calc(100vw - 32px)', sm: 460 },
              borderRadius: 2,
              boxShadow: '0 10px 24px rgba(0,0,0,0.25)',
              alignItems: 'center',
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 700,
              ...(toastSeverity === 'success' && {
                bgcolor: '#0f56b3',
                color: '#fff',
                '& .MuiAlert-icon': { color: '#fff' },
              }),
              ...(toastSeverity === 'info' && {
                bgcolor: '#0f56b3',
                color: '#fff',
                '& .MuiAlert-icon': { color: '#fff' },
              }),
              ...(toastSeverity === 'warning' && {
                bgcolor: '#ffc400',
                color: '#111',
                '& .MuiAlert-icon': { color: '#111' },
              }),
              ...(toastSeverity === 'error' && {
                bgcolor: '#b90d0d',
                color: '#fff',
                '& .MuiAlert-icon': { color: '#fff' },
              }),
            }}
          >
            {toastText}
          </Alert>
        </Snackbar>

        <Tooltip title="Reset laboratory">
          <IconButton
            onClick={resetLaboratory}
            aria-label="Reset Laboratory"
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 5,
              width: 40,
              height: 40,
              bgcolor: 'rgba(255,255,255,0.94)',
              border: '1px solid rgba(14,79,183,0.18)',
              color: '#0E4FB7',
              boxShadow: '0 10px 22px rgba(0,0,0,0.16)',
              '&:hover': {
                bgcolor: '#ffffff',
              },
            }}
          >
            <AutorenewRoundedIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>

        <Box
          sx={{
            position: 'absolute',
            top: 18,
            left: 18,
            zIndex: 4,
            px: 1.2,
            py: 0.65,
            borderRadius: '999px',
            backgroundColor: 'rgba(255,255,255,0.92)',
            border: '1px solid rgba(14,79,183,0.12)',
            boxShadow: '0 8px 18px rgba(0,0,0,0.10)',
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontWeight: 800,
              fontSize: '0.8rem',
              color: '#0E4FB7',
              letterSpacing: '0.03em',
            }}
          >
            {selectedLabConfig.label}
          </Typography>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
          }}
        >
          <Box
            component="img"
            src={LoopAnimation}
            alt="Waiting for professor RFID"
            sx={{
              width: 'min(1000px, 100vw)',
              borderRadius: 2,
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          />
        </Box>

        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 18,
            px: 2,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              mt: -14,
              opacity: 0.9,
              fontFamily: '"Poppins", sans-serif',
              fontSize: '22px',
              color: '#888',
              fontWeight: 800,
            }}
          >
            {profLoading ? 'Preparing session setup...' : '( Tap the Professor’s ID to proceed. )'}
          </Typography>

          {profLoading && (
            <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={22} />
            </Box>
          )}

          {!!profError && (
            <Typography
              sx={{
                mt: 1.2,
                fontFamily: '"Poppins", sans-serif',
                fontSize: '0.95rem',
                color: '#b90d0d',
                fontWeight: 700,
              }}
            >
              {profError}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: PAGE_BG,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 3, md: 4 },
      }}
    >
      <Snackbar
        open={toastOpen}
        autoHideDuration={5000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        TransitionComponent={SlideUpTransition}
        sx={{
          '&.MuiSnackbar-root': {
            left: { xs: 16, sm: 24 },
            bottom: { xs: 16, sm: 24 },
          },
        }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          variant="filled"
          sx={{
            width: '100%',
            minWidth: { xs: 260, sm: 360 },
            maxWidth: { xs: 'calc(100vw - 32px)', sm: 460 },
            borderRadius: 2,
            boxShadow: '0 10px 24px rgba(0,0,0,0.25)',
            alignItems: 'center',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 700,
            ...(toastSeverity === 'success' && {
              bgcolor: '#0f56b3',
              color: '#fff',
              '& .MuiAlert-icon': { color: '#fff' },
            }),
            ...(toastSeverity === 'info' && {
              bgcolor: '#0f56b3',
              color: '#fff',
              '& .MuiAlert-icon': { color: '#fff' },
            }),
            ...(toastSeverity === 'warning' && {
              bgcolor: '#ffc400',
              color: '#111',
              '& .MuiAlert-icon': { color: '#111' },
            }),
            ...(toastSeverity === 'error' && {
              bgcolor: '#b90d0d',
              color: '#fff',
              '& .MuiAlert-icon': { color: '#fff' },
            }),
          }}
        >
          {toastText}
        </Alert>
      </Snackbar>

      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: '980px',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 900,
            mx: 'auto',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '0.95fr 1.05fr' },
            gap: 2.2,
            alignItems: 'stretch',
          }}
        >
          <Box sx={{ display: 'grid', gap: 2.2 }}>
            <Box
              sx={{
                backgroundColor: 'rgba(242,188,9,0.96)',
                borderRadius: '18px',
                border: '4px solid #ffffff',
                boxShadow: '0 18px 40px rgba(0,0,0,0.12)',
                p: { xs: 2, sm: 2.5, md: 3 },
                height: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.6 }}>
                <ApartmentOutlinedIcon sx={{ color: '#0E4FB7' }} />
                <Typography
                  sx={{
                    fontFamily: '"Space Grotesk", sans-serif',
                    fontWeight: 900,
                    fontSize: { xs: '1rem', sm: '1.08rem' },
                    color: '#0E4FB7',
                    letterSpacing: '0.03em',
                  }}
                >
                  LABORATORY
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: 1.4,
                }}
              >
                {LAB_OPTIONS.map((lab) => {
                  const isSelected = selectedLab === lab.label || selectedLab === lab.key;
                  const previewImage = isSelected
                    ? lab.images[bgIndex % lab.images.length] || lab.images[0]
                    : lab.images[0];

                  return (
                    <Box
                      key={lab.key}
                      sx={{
                        minHeight: 118,
                        borderRadius: '16px',
                        overflow: 'hidden',
                        p: 0,
                        border: isSelected ? '3px solid #0E4FB7' : '2px solid #ffffff',
                        position: 'relative',
                        backgroundImage: `linear-gradient(rgba(0,0,0,0.24), rgba(0,0,0,0.24)), url(${previewImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        boxShadow: isSelected
                          ? '0 0 0 4px rgba(14,79,183,0.14)'
                          : '0 10px 20px rgba(0,0,0,0.10)',
                        transition: 'background-image 0.4s ease',
                        pointerEvents: 'none',
                        userSelect: 'none',
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 1.5,
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#fff',
                            fontFamily: '"Advetime", sans-serif',
                            fontWeight: 700,
                            fontSize: { xs: '1.35rem', sm: '1.3rem', md: '3rem' },
                            textAlign: 'center',
                            textShadow: '0 4px 14px rgba(0,0,0,0.45)',
                            letterSpacing: '0.03em',
                            lineHeight: 1.1,
                            opacity: 0.77,
                          }}
                        >
                          {lab.label}
                        </Typography>
                      </Box>

                      {isSelected && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            px: 1,
                            py: 0.35,
                            borderRadius: '999px',
                            backgroundColor: '#0E4FB7',
                            color: '#fff',
                            fontFamily: '"Space Grotesk", sans-serif',
                            fontWeight: 800,
                            fontSize: '0.72rem',
                            letterSpacing: '0.02em',
                          }}
                        >
                          SELECTED
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2.2,
              height: '100%',
            }}
          >
            <Box
              sx={{
                backgroundColor: 'rgba(242,188,9,0.96)',
                borderRadius: '18px',
                border: '4px solid #ffffff',
                boxShadow: '0 18px 40px rgba(0,0,0,0.12)',
                p: { xs: 2, sm: 2.5, md: 3 },
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.6, mt: 0 }}>
                <InfoOutlinedIcon sx={{ color: '#0E4FB7' }} />
                <Typography
                  sx={{
                    fontFamily: '"Space Grotesk", sans-serif',
                    fontWeight: 900,
                    fontSize: { xs: '1rem', sm: '1.08rem' },
                    color: '#0E4FB7',
                    letterSpacing: '0.03em',
                  }}
                >
                  SESSION INFO
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gap: 2 }}>
                <FormControl fullWidth sx={fieldSx}>
                  <Select
                    autoWidth={false}
                    IconComponent={() => null}
                    value={selected.course}
                    onChange={(e) => handleChange(e, 'course')}
                    aria-label="Select Course"
                    displayEmpty
                    startAdornment={
                      <InputAdornment position="start" sx={{ ml: 1.2, mr: 1.2 }}>
                        <SchoolOutlinedIcon sx={{ color: '#D9AB24' }} />
                      </InputAdornment>
                    }
                    endAdornment={
                      <InputAdornment position="end" sx={{ mr: 1.2 }}>
                        {iconCluster}
                      </InputAdornment>
                    }
                    renderValue={(value) => renderClosedValue(value, 'Select Course')}
                    MenuProps={dropdownMenuProps}
                  >
                    {sortedCourses.map((course) => (
                      <MenuItem key={course} value={course} sx={{ fontFamily: '"Poppins", sans-serif' }}>
                        <Box sx={ellipsisBoxSx} title={course}>
                          {course}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={fieldSx}>
                  <Select
                    autoWidth={false}
                    IconComponent={() => null}
                    value={selected.yearLevel}
                    onChange={(e) => handleChange(e, 'yearLevel')}
                    aria-label="Select Year Level"
                    displayEmpty
                    startAdornment={
                      <InputAdornment position="start" sx={{ ml: 1.2, mr: 1.2 }}>
                        <CalendarTodayOutlinedIcon sx={{ color: '#D9AB24' }} />
                      </InputAdornment>
                    }
                    endAdornment={
                      <InputAdornment position="end" sx={{ mr: 1.2 }}>
                        {iconCluster}
                      </InputAdornment>
                    }
                    renderValue={(value) => renderClosedValue(value, 'Select Year Level')}
                    MenuProps={dropdownMenuProps}
                  >
                    {sortedYearLevels.map((lvl) => (
                      <MenuItem key={lvl} value={lvl} sx={{ fontFamily: '"Poppins", sans-serif' }}>
                        <Box sx={ellipsisBoxSx} title={lvl}>
                          {lvl}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={fieldSx}>
                  <Select
                    autoWidth={false}
                    IconComponent={() => null}
                    value={selected.section}
                    onChange={(e) => handleChange(e, 'section')}
                    aria-label="Select Section"
                    displayEmpty
                    startAdornment={
                      <InputAdornment position="start" sx={{ ml: 1.2, mr: 1.2 }}>
                        <Groups2OutlinedIcon sx={{ color: '#D9AB24' }} />
                      </InputAdornment>
                    }
                    endAdornment={
                      <InputAdornment position="end" sx={{ mr: 1.2 }}>
                        {iconCluster}
                      </InputAdornment>
                    }
                    renderValue={(value) => renderClosedValue(value, 'Select Section')}
                    MenuProps={dropdownMenuProps}
                  >
                    {sortedSections.map((sec) => (
                      <MenuItem key={sec} value={sec} sx={{ fontFamily: '"Poppins", sans-serif' }}>
                        <Box sx={ellipsisBoxSx} title={sec}>
                          {sec}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Autocomplete
                  freeSolo
                  options={filteredSubjects}
                  value={selectedSubjectObj}
                  inputValue={subjectInputValue}
                  disabled={!selected.course}
                  onInputChange={(_, newInputValue, reason) => {
                    setSubjectInputValue(newInputValue);

                    if (reason === 'input') {
                      setSelected((prev) => ({
                        ...prev,
                        subject: '',
                      }));
                    }
                  }}
                  onChange={(_, newValue) => {
                    if (!newValue || typeof newValue === 'string') {
                      setSelected((prev) => ({
                        ...prev,
                        subject: '',
                      }));
                      return;
                    }

                    setSelected((prev) => ({
                      ...prev,
                      subject: newValue.code || newValue.description || '',
                    }));
                    setSubjectInputValue(newValue.description || '');
                    setError('');
                  }}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option?.description || '';
                  }}
                  isOptionEqualToValue={(option, value) =>
                    (option?.code || option?.description || '') ===
                    (value?.code || value?.description || '')
                  }
                  filterOptions={(options, state) => {
                    const search = normalizeText(state.inputValue);
                    const filtered = options.filter((option) =>
                      normalizeText(option?.description).includes(search)
                    );
                    return filtered;
                  }}
                  noOptionsText={
                    selected.course
                      ? 'No subjects available for this course'
                      : 'Select course first'
                  }
                  popupIcon={null}
                  clearIcon={null}
                  ListboxProps={{
                    sx: {
                      maxHeight: ITEM_HEIGHT * MAX_VISIBLE_ITEMS + 12,
                      overflowY: 'auto',
                      paddingTop: '6px',
                      paddingBottom: '6px',
                    },
                  }}
                  slotProps={{
                    paper: {
                      elevation: 0,
                      sx: menuPaperSx,
                    },
                  }}
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      minHeight: { xs: 58, md: 56 },
                      borderRadius: '14px',
                      backgroundColor: '#F0F0F0',
                      fontFamily: '"Poppins", sans-serif',
                      fontSize: { xs: '0.98rem', md: '1.02rem' },
                      color: '#171717',
                      boxShadow: 'none',
                      pr: '48px !important',
                      '& fieldset': {
                        borderColor: '#0E4FB7',
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: '#0E4FB7',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#0E4FB7',
                        borderWidth: '2px',
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontFamily: '"Poppins", sans-serif !important',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                    '& .MuiAutocomplete-endAdornment': {
                      display: 'none',
                    },
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.code || option.description}>
                      <Box sx={ellipsisBoxSx} title={option.description || 'Unnamed Subject'}>
                        {option.description || 'Unnamed Subject'}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Subject"
                      error={Boolean(subjectInputValue) && !isValidSubjectSelection}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start" sx={{ ml: 1.2, mr: 1.2 }}>
                              <BookOutlinedIcon sx={{ color: '#D9AB24' }} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        endAdornment: (
                          <InputAdornment position="end" sx={{ mr: -3 }}>
                            {iconCluster}
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                gap: 1.5,
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
              }}
            >
              <Tooltip title="Change professor">
                <IconButton
                  onClick={resetProfessor}
                  aria-label="Reset Professor"
                  sx={{
                    width: { xs: '100%', sm: 76 },
                    height: 76,
                    bgcolor: '#0E4FB7',
                    border: '3px solid #ffffff',
                    color: '#fff',
                    borderRadius: '14px',
                    boxShadow: '0 14px 28px rgba(0,0,0,0.14)',
                    flexShrink: 0,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.22s ease',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.04))',
                      opacity: 1,
                    },
                    '&:hover': {
                      bgcolor: '#0B459F',
                      transform: 'translateY(-2px) scale(1.02)',
                      boxShadow: '0 18px 32px rgba(0,0,0,0.18)',
                    },
                    '&:active': {
                      transform: 'translateY(0) scale(0.98)',
                    },
                  }}
                >
                  <AutorenewRoundedIcon
                    sx={{
                      position: 'relative',
                      zIndex: 1,
                      fontSize: 31,
                      color: '#fff',
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.25))',
                    }}
                  />
                </IconButton>
              </Tooltip>

              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading || !isValidSubjectSelection}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: 76,
                  borderRadius: '14px',
                  backgroundColor: '#0E4FB7',
                  border: '3px solid #ffffff',
                  boxShadow: '0 14px 28px rgba(0,0,0,0.14)',
                  fontFamily: '"Space Grotesk", sans-serif',
                  fontWeight: 900,
                  fontSize: { xs: '1.2rem', sm: '1.45rem', md: '1.6rem' },
                  letterSpacing: '0.03em',
                  color: '#fff',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.22s ease',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.04))',
                    opacity: 1,
                  },
                  '&:hover': {
                    backgroundColor: '#0B459F',
                    transform: 'translateY(-2px) scale(1.02)',
                    boxShadow: '0 18px 32px rgba(0,0,0,0.18)',
                  },
                  '&:active': {
                    transform: 'translateY(0) scale(0.98)',
                  },
                  '&:disabled': {
                    backgroundColor: '#5D7FC6',
                    color: '#F4F4F4',
                  },
                }}
              >
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center' }}>
                  {loading ? <CircularProgress size={28} color="inherit" /> : 'START SESSION'}
                </Box>
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SessionSelection;